import { CommissaryTransfer } from '@reliance/types/models/CommissaryTransfer';
import { Facility } from '@reliance/types/models/Facility';
import { createColumnHelper } from '@tanstack/react-table';
import { AxiosError } from 'axios';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import AddTrustAccountTransaction from '../commissary-transfer/AddTrustAccountTransaction';
import ErrorMessage from '../common/ErrorMessage';
import { formatCurrency } from '../format/Currency';
import { formatDate } from '../format/DateTime';
import { useCommissaryTransfersByFacility } from '../hooks/CommissaryTransfer';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import InmateDetailBlock from '../inmate/InmateDetailBlock';
import BaseHtmlTable from '../table/BaseHtmlTable';

type FacilityTrustAccountTabProps = {
  facility: Facility;
};

const TrustAccountTransactionsTable: React.FC<{ facility: Facility }> = ({ facility }) => {
  const { result, loading, error } = useCommissaryTransfersByFacility(facility.id);

  useNotifyTabLoading('trust-account', loading);

  const transactions = useMemo(() => result?.data || [], [result?.data]);

  const columnHelper = createColumnHelper<CommissaryTransfer>();
  const columnDefs = [
    columnHelper.accessor(
      (row) =>
        row.dateEntered ? moment(row.dateEntered.date).format('MM/DD/YYYY HH:mm') : 'N/A',
      {
        id: 'dateEntered',
        header: () => 'Date',
      }
    ),
    columnHelper.accessor((row) => `${row.inmate?.firstName} ${row.inmate?.lastName}`, {
      id: 'inmate',
      header: () => 'Inmate',
      cell: (row) => <InmateDetailBlock inmate={row.row.original.inmate} />,
    }),
    columnHelper.accessor('description', {
      id: 'description',
      header: () => 'Description',
      cell: (row) => row.getValue() || 'N/A',
    }),
    columnHelper.accessor('enteredBy', {
      id: 'enteredBy',
      header: () => 'Entered By',
      cell: (row) => row.getValue() || 'N/A',
    }),
    columnHelper.accessor((row) => parseFloat(row.activity?.amount?.toString() || '0'), {
      id: 'amount',
      header: () => 'Amount',
      cell: (row) => (
        <span className={row.getValue() < 0 ? 'text-success' : 'text-danger'}>
          {formatCurrency(row.getValue() * -1)}
        </span>
      ),
    }),
    columnHelper.accessor((row) => row.dateReversed != null, {
      id: 'status',
      header: () => 'Status',
      cell: (row) =>
        row.row.original.dateReversed ? (
          <span className="badge bg-secondary">
            Reversed: {formatDate(row.row.original.dateReversed)}
          </span>
        ) : null,
    }),
  ];

  if (error) {
    return <ErrorMessage error={error as AxiosError} />;
  }

  return <BaseHtmlTable loading={loading} columnDefs={columnDefs} rows={transactions} />;
};

const FacilityTrustAccountTab: React.FC<FacilityTrustAccountTabProps> = ({ facility }) => {
  const [refreshKey, setRefreshKey] = useState(0);

  const handleTransactionAdded = () => {
    setRefreshKey((prev) => prev + 1);
  };

  return (
    <Card>
      <Card.Body>
        <Row className="mb-4">
          <Col>
            <AddTrustAccountTransaction facility={facility} onTransactionAdded={handleTransactionAdded} />
          </Col>
        </Row>

        <Row>
          <Col>
            <TrustAccountTransactionsTable key={refreshKey} facility={facility} />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export const facilityTrustAccountTabMeta = {
  heading: 'Trust Account',
  style: 'fas fa-fw fa-lg fa-piggy-bank',
  permissions: ['commissary_transfer_manage'],
};

export default FacilityTrustAccountTab;
